import { useRef, type KeyboardEvent, type ReactNode } from "react";
import * as Tooltip from "@radix-ui/react-tooltip";

import { cn } from "@/lib/utils";

export interface PreviewChoiceOption<Value extends string> {
  value: Value;
  label: string;
  /** One sentence, in a tooltip: what this choice looks like for viewers. */
  description: string;
  /** A small drawing of the result (see `PreviewFrame`). */
  preview: (selected: boolean) => ReactNode;
  disabled?: boolean;
}

/**
 * A single choice shown as small pictures with a label under each, as the
 * Layout setting of the UI settings: for choices that are easier to see than to
 * describe. It behaves as a radio group (one tab stop, arrow keys move and select).
 */
export function PreviewChoice<Value extends string>({
  label,
  value,
  options,
  onChange,
  className,
}: {
  /** Accessible name of the group. */
  label: string;
  value: Value;
  options: readonly PreviewChoiceOption<Value>[];
  onChange: (value: Value) => void;
  /** The grid of the cards; two per row by default. */
  className?: string;
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const enabled = options
    .map((option, index) => (option.disabled ? -1 : index))
    .filter((index) => index >= 0);
  const selectedIndex = options.findIndex((option) => option.value === value);
  const tabStop = selectedIndex >= 0 ? selectedIndex : (enabled[0] ?? -1);

  function move(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const step =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
    if (!step || enabled.length === 0) return;
    event.preventDefault();
    const position = enabled.indexOf(index);
    const next = enabled[(position + step + enabled.length) % enabled.length];
    onChange(options[next].value);
    refs.current[next]?.focus();
  }

  return (
    <Tooltip.Provider delayDuration={120}>
      <div
        role="radiogroup"
        aria-label={label}
        className={cn("grid w-full grid-cols-1 gap-2 sm:grid-cols-2", className)}
      >
        {options.map((option, index) => {
          const selected = index === selectedIndex;
          return (
            <Tooltip.Root key={option.value}>
              <Tooltip.Trigger asChild>
                <button
                  ref={(element) => {
                    refs.current[index] = element;
                  }}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-description={option.description}
                  disabled={option.disabled}
                  tabIndex={index === tabStop ? 0 : -1}
                  onClick={() => onChange(option.value)}
                  onKeyDown={(event) => move(event, index)}
                  className={cn(
                    "group rounded-xl border p-2 text-center transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                    selected
                      ? "border-brand-cyan bg-brand-cyan/10 text-primary"
                      : "border-border bg-background/70 text-secondary hover:bg-surface-elevated hover:text-primary",
                  )}
                >
                  <div className="flex justify-center">{option.preview(selected)}</div>
                  <div className="mt-2 text-2xs font-medium">{option.label}</div>
                </button>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  side="top"
                  sideOffset={8}
                  className="z-70 max-w-55 rounded-lg border border-border bg-surface px-2.5 py-2 text-2xs leading-4 text-secondary shadow-xl"
                >
                  {option.description}
                  <Tooltip.Arrow className="fill-surface" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          );
        })}
      </div>
    </Tooltip.Provider>
  );
}

/** The colors of a preview drawing, quiet until its card is chosen. */
export function previewInks(selected: boolean) {
  return {
    accent: selected ? "var(--brand-cyan)" : "var(--text-subtle)",
    frame: selected ? "var(--text)" : "var(--border-strong)",
    surface: selected ? "var(--row-selected)" : "var(--surface-nav)",
    deep: selected ? "var(--surface-topbar)" : "var(--text-subtle)",
  };
}

/**
 * The screen a preview is drawn in: an 84×56 rounded frame, drawn small (a
 * choice read at a glance) or large (a layout whose details matter).
 */
export function PreviewFrame({
  selected,
  size = "sm",
  children,
}: {
  selected: boolean;
  size?: "sm" | "lg";
  children: ReactNode;
}) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 84 56"
      className={cn("shrink-0 rounded-md", size === "lg" ? "h-20 w-30" : "h-12 w-18")}
    >
      <rect
        x="1"
        y="1"
        width="82"
        height="54"
        rx="8"
        fill="var(--surface)"
        stroke={previewInks(selected).frame}
        strokeWidth="1.5"
      />
      {children}
    </svg>
  );
}
